import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { hexToRgba } from '@e3dc-react/theme'
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'
import React from 'react'
import { useWatch } from 'react-hook-form'
import { SettingsBox } from 'src/Elements/Data'
import { NumberField } from 'src/Libs/inputs/react-hook-form'
import { REACTIVE_POWER_FUNCTION_MODEL } from '../../types/common'
import { ReactivePowerSettingsModel } from '../../types/grid-and-system-protection.model'
import { ReactivePowerSettingsForm } from '../../types/schemas'
import { getChangedFieldStyle } from '../../utils'
import { NAME_SPACE } from './constants'
import ReactivePowerSettingsTableRow from './ReactivePowerSettingsTableRow'

const ReactivePowerSettingsTable: React.FC<{ reactivePowerSettingsModel: ReactivePowerSettingsModel }> = ({ reactivePowerSettingsModel }) => {
  const { t } = useCustomTranslation()
  const selectedFunction: REACTIVE_POWER_FUNCTION_MODEL = useWatch({ name: 'selectedFunction' }) || REACTIVE_POWER_FUNCTION_MODEL.cosPhi
  const allFulcrums: ReactivePowerSettingsForm['fulcrums'] = useWatch({ name: 'fulcrums' })

  const selectedFulcrums = allFulcrums?.[selectedFunction] || []
  const minCosPhi = reactivePowerSettingsModel.minCosPhi
  const displayMinCosPhi =
    (selectedFunction === REACTIVE_POWER_FUNCTION_MODEL.fixedQ || selectedFunction === REACTIVE_POWER_FUNCTION_MODEL.qU) && minCosPhi
  const tableHeaderValues = tableHeaderConfig[selectedFunction]

  if (selectedFulcrums.length === 0) return null
  return (
    <>
      {displayMinCosPhi && (
        <SettingsBox
          title={t(`${NAME_SPACE}.min-cos-phi.title`)}
          suffix={
            <NumberField
              name="minCosPhi"
              // disabled={isFieldDisabled({ min: minCosPhi.min, max: minCosPhi.max, value: minCosPhi.value })} this should be enabled once the minCosPhi field is fixed
              disabled
              sx={getChangedFieldStyle(minCosPhi.changed)}
            />
          }
        />
      )}
      <TableContainer
        key={selectedFunction}
        sx={theme => ({
          [theme.breakpoints.down('sm')]: {
            overflowX: 'scroll',
            '&::-webkit-scrollbar': {
              height: 6,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: hexToRgba(theme.palette.grey.grey01, 0.1),
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: hexToRgba(theme.palette.grey.grey01, 0.3),
              borderRadius: theme.shape.borderRadius,
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: hexToRgba(theme.palette.grey.grey01, 0.5),
            },
          },
        })}
      >
        <Table
          sx={theme => ({
            border: 'none',
            '& td, & th': {
              border: 'none',
              padding: 1,
            },
            '& tbody tr, & thead tr': {
              border: 'none',
            },

            [theme.breakpoints.down('sm')]: {
              tableLayout: 'fixed',
            },
          })}
        >
          <TableHead>
            <TableRow>
              <TableCell align="left">{t(`${NAME_SPACE}.fulcrums.header.fulcrum`)}</TableCell>
              <TableCell align="left">{tableHeaderValues.secondColumn}</TableCell>
              <TableCell align="left">{tableHeaderValues.thirdColumn}</TableCell>
              <TableCell align="left">{t(`${NAME_SPACE}.fulcrums.header.phase-shift`)}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {selectedFulcrums.map((fulcrum, index) => (
              <ReactivePowerSettingsTableRow
                key={`${selectedFunction}-${index}`}
                index={index}
                selectedFunction={selectedFunction}
                fulcrum={fulcrum}
                fulcrums={reactivePowerSettingsModel.fulcrums}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  )
}

export default ReactivePowerSettingsTable

const tableHeaderConfig = {
  [REACTIVE_POWER_FUNCTION_MODEL.fixCosPhi]: {
    secondColumn: 'P/Pn',
    thirdColumn: 'cos φ',
  },
  [REACTIVE_POWER_FUNCTION_MODEL.cosPhi]: {
    secondColumn: 'P/Pn',
    thirdColumn: 'cos φ',
  },
  [REACTIVE_POWER_FUNCTION_MODEL.qU]: {
    secondColumn: 'U/Un',
    thirdColumn: 'Q/Sn',
  },
  [REACTIVE_POWER_FUNCTION_MODEL.fixedQ]: {
    secondColumn: '',
    thirdColumn: 'var',
  },
}
